import { useMemo, useState } from 'react';
import { Check, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { useInstallPlugin, useSyncPlugin } from '@/hooks/usePlugins';
import { useToast } from '@/components/ui/Toast';
import type { Plugin } from '@/types';
import type {
  OrgPluginCatalogEntry,
  OrgPluginCatalogSource,
} from '@/types/plugins';
import { PluginGridCard, TypeIcon } from './PluginGridCard';

interface Props {
  entries: OrgPluginCatalogEntry[];
  installed: Plugin[];
  sources: OrgPluginCatalogSource[];
}

const ALL_CATEGORIES = '__all__';

/**
 * Stable cross-reference key used to mark catalog cards as "Installed ✓".
 * Lowercased + `.git`-stripped repoUrl plus subdir (`""` = repo root) — both
 * the Plugin and the catalog entry are normalized the same way so a string
 * comparison just works.
 */
function installKey(repoUrl: string, subdir: string | undefined): string {
  const url = repoUrl.trim().toLowerCase().replace(/\.git$/, '');
  return `${url}::${subdir ?? ''}`;
}

/** Same normalization used to join catalog entries to source labels. */
function normalizeRepoUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\.git$/, '');
}

/**
 * True when the catalog `candidate` version is strictly newer than the
 * `installed` one. Both are dotted-numeric (semver-ish) strings, optionally
 * `v`-prefixed. A missing or non-numeric version on either side compares as
 * "not newer" so a malformed catalog entry never spuriously offers an update.
 */
function isNewerVersion(
  installed: string | undefined,
  candidate: string | undefined,
): boolean {
  if (!installed || !candidate) return false;
  const parse = (v: string) =>
    v
      .trim()
      .replace(/^v/i, '')
      .split('.')
      .map((n) => parseInt(n, 10));
  const a = parse(installed);
  const b = parse(candidate);
  if (a.some(Number.isNaN) || b.some(Number.isNaN)) return false;
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (bi > ai) return true;
    if (bi < ai) return false;
  }
  return false;
}

/** "developer-tools" → "Developer tools". */
function formatCategory(slug: string): string {
  const spaced = slug.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function CatalogPluginsList({
  entries,
  installed,
  sources,
}: Props) {
  // key → installed Plugin, so each catalog card can compare its catalog
  // version against what's actually installed and offer an Update.
  const installedByKey = useMemo(() => {
    const map = new Map<string, Plugin>();
    for (const p of installed) {
      map.set(installKey(p.source.repoUrl, p.source.subdir), p);
    }
    return map;
  }, [installed]);

  // repoUrl → marketplace label, used as a per-card "from {marketplace}" tag.
  // Falls back to undefined when no source matches or the source has no label.
  const labelByRepo = useMemo(() => {
    const map = new Map<string, string>();
    for (const source of sources) {
      const label = source.label?.trim();
      if (!label) continue;
      map.set(normalizeRepoUrl(source.repoUrl), label);
    }
    return map;
  }, [sources]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const entry of entries) {
      if (entry.category) set.add(entry.category);
    }
    return Array.from(set).sort();
  }, [entries]);

  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);

  const visible = useMemo(() => {
    if (activeCategory === ALL_CATEGORIES) return entries;
    return entries.filter((e) => e.category === activeCategory);
  }, [entries, activeCategory]);

  if (entries.length === 0) return null;

  const showFilter = categories.length > 1;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm text-muted-foreground">Available from catalog</h2>
        {showFilter && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <CategoryChip
              label="All"
              count={entries.length}
              active={activeCategory === ALL_CATEGORIES}
              onClick={() => setActiveCategory(ALL_CATEGORIES)}
            />
            {categories.map((cat) => {
              const count = entries.filter((e) => e.category === cat).length;
              return (
                <CategoryChip
                  key={cat}
                  label={formatCategory(cat)}
                  count={count}
                  active={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                />
              );
            })}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((entry) => {
          const key = installKey(entry.repoUrl, entry.subdir);
          return (
            <CatalogEntryCard
              key={key}
              entry={entry}
              installed={installedByKey.get(key)}
              marketplaceLabel={labelByRepo.get(normalizeRepoUrl(entry.repoUrl))}
            />
          );
        })}
      </div>
    </section>
  );
}

interface CategoryChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function CategoryChip({ label, count, active, onClick }: CategoryChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
        active
          ? 'bg-foreground text-background border-foreground'
          : 'bg-background text-muted-foreground border-border hover:bg-accent hover:text-foreground'
      }`}
    >
      {label}
      <span className={active ? 'opacity-70' : 'opacity-60'}>{count}</span>
    </button>
  );
}

interface CardProps {
  entry: OrgPluginCatalogEntry;
  /** The matching installed plugin, when this catalog entry is installed. */
  installed?: Plugin;
  marketplaceLabel?: string;
}

function CatalogEntryCard({ entry, installed, marketplaceLabel }: CardProps) {
  const install = useInstallPlugin();
  const sync = useSyncPlugin();
  const { success, error: showError } = useToast();

  const isInstalled = !!installed;
  const updateAvailable =
    !!installed && isNewerVersion(installed.version, entry.version);
  const isUpdating =
    !!installed && (installed.status === 'syncing' || sync.isPending);

  const handleUpdate = async () => {
    if (!installed) return;
    try {
      await sync.mutateAsync(installed.pluginId);
      success('Update started');
      // Completion toast comes from usePluginEventBus mounted on PluginList.
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to update plugin');
    }
  };

  const handleInstall = async () => {
    try {
      await install.mutateAsync({
        mode: 'plugin',
        repoUrl: entry.repoUrl,
        ref: entry.ref,
        subdir: entry.subdir,
        // One-click installs always opt into auto-update. Backend ignores it
        // for SHA refs, so no client-side gating needed.
        autoUpdate: true,
        metadata: {
          name: entry.name,
          displayName: entry.displayName,
          version: entry.version,
          description: entry.description,
          author: entry.author,
        },
      });
      // Completion toast comes from usePluginEventBus mounted on PluginList.
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to install plugin');
    }
  };

  const authorName = entry.author?.name?.trim();
  const metaText = [
    marketplaceLabel,
    entry.version ? `v${entry.version}` : undefined,
    authorName ? `by ${authorName}` : undefined,
  ]
    .filter((p): p is string => !!p)
    .join(' · ');

  let action: React.ReactNode;
  if (updateAvailable) {
    action = (
      <Button
        size="sm"
        onClick={handleUpdate}
        isLoading={isUpdating}
        title={`Update to v${entry.version} from v${installed?.version}`}
      >
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        {isUpdating ? 'Updating' : 'Update'}
      </Button>
    );
  } else if (isInstalled) {
    action = (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Check className="h-3.5 w-3.5" />
        Installed
      </span>
    );
  } else {
    action = (
      <Button
        size="sm"
        variant="outline"
        onClick={handleInstall}
        isLoading={install.isPending}
      >
        <Download className="h-3.5 w-3.5 mr-1.5" />
        Install
      </Button>
    );
  }

  return (
    <PluginGridCard
      icon={<TypeIcon type={entry.type} />}
      title={entry.displayName ?? entry.name}
      description={entry.description?.trim() || entry.name}
      repoUrl={entry.repoUrl}
      subdir={entry.subdir}
      metaText={metaText || undefined}
      action={action}
    />
  );
}
